// React
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'

// Libraries
import { StyleSheetManager } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronLeft, faChevronRight, faArrowLeft } from '@fortawesome/pro-regular-svg-icons'

// Components
import BLightboxGallery from './styles'
import OptimizedImage from '../OptimizedImage'

// Shared
import { shouldForwardProp } from 'shared/utils/shouldForwardProp'

const THUMBNAIL_SIZE = 80
const HORIZONTAL_THRESHOLD = 50
const MOBILE_BREAKPOINT = 577

/**
 * LightboxGallery - A full-screen modal gallery component for displaying product images
 *
 * @component
 * @example
 * const photos = [
 *   { id: '1', url: 'https://...', coordinates: { x: 0, y: 0 } },
 *   { id: '2', url: 'https://...', coordinates: { x: 0.5, y: 0.5 } }
 * ]
 *
 * <LightboxGallery
 *   photos={photos}
 *   selectedIndex={0}
 *   onClose={() => setShowLightbox(false)}
 *   itemTitle="Product Name"
 * />
 *
 * @param {Object} props - Component props
 * @param {Array<{id: string, url: string, coordinates: Object}>} props.photos - Array of photo objects to display
 * @param {number} [props.selectedIndex=0] - Initial index of the image to display
 * @param {Function} props.onClose - Callback function invoked when the lightbox is closed
 * @param {string} [props.itemTitle] - Optional title to display in the lightbox header
 * @returns {React.Portal} Portal-rendered lightbox component
 */
const LightboxGallery = ({
  photos,
  selectedIndex,
  onClose,
  itemTitle
}) => {
  const [currentIndex, setCurrentIndex] = useState(selectedIndex || 0)
  const [swipe, setSwipe] = useState({})
  const [swiping, setSwiping] = useState(false)
  const [galleryOffset, setGalleryOffset] = useState(0)

  useEffect(() => {
    if (selectedIndex !== null && selectedIndex !== undefined) {
      setCurrentIndex(selectedIndex)
      setGalleryOffset(Math.min(selectedIndex, getMaximumOffset()))
    }
  }, [selectedIndex])

  useEffect(() => {
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden'

    return () => document.body.style.overflow = ''
  }, [])

  const onTouchStart = (e) => {
    const touch = e.touches[0]
    setSwipe({ x: touch.clientX })
  }

  const onTouchMove = (e) => {
    if (e.changedTouches && e.changedTouches.length) setSwiping(true)
  }

  const onTouchEnd = (e) => {
    const touch = e.changedTouches[0]
    const x = touch.clientX - swipe.x

    if (swiping && x > HORIZONTAL_THRESHOLD) handlePrevious()
    else if (swiping && x < -HORIZONTAL_THRESHOLD) handleNext()

    setSwiping(false)
  }

  const getVisibleThumbnailCount = () => window.innerWidth >= MOBILE_BREAKPOINT ? 5 : 3

  const getMaximumOffset = () => {
    const visibleCount = getVisibleThumbnailCount()
    return Math.max(photos.length - visibleCount, 0)
  }

  const handleNext = () => {
    if (currentIndex < photos.length - 1) {
      const newIndex = currentIndex + 1
      setCurrentIndex(newIndex)

      const maxOffset = getMaximumOffset()
      const visibleCount = getVisibleThumbnailCount()

      if (photos.length > visibleCount && newIndex > galleryOffset + visibleCount - 1) {
        setGalleryOffset(Math.min(newIndex - visibleCount + 1, maxOffset))
      }
    }
  }

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1
      setCurrentIndex(newIndex)

      const visibleCount = getVisibleThumbnailCount()

      if (photos.length > visibleCount && newIndex < galleryOffset) {
        setGalleryOffset(Math.max(0, newIndex))
      }
    }
  }

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index)

    const maxOffset = getMaximumOffset()
    const visibleCount = getVisibleThumbnailCount()

    if (photos.length > visibleCount) {
      const targetOffset = Math.max(0, Math.min(index - Math.floor(visibleCount / 2), maxOffset))
      setGalleryOffset(targetOffset)
    }
  }

  const handleClose = (e) => {
    e?.preventDefault()
    e?.stopPropagation()
    onClose?.()
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose(e)
  }

  if (!photos || photos.length === 0) return null

  const currentPhoto = photos[currentIndex]
  const showGallery = photos.length > 0
  const showControls = photos.length > 1

  const lightboxContent = (
    <StyleSheetManager
      target={document.head}
      shouldForwardProp={shouldForwardProp}
      enableVendorPrefixes
    >
      <BLightboxGallery onClick={handleBackdropClick}>
        <BLightboxGallery.Wrapper>
          <BLightboxGallery.Header>
            <BLightboxGallery.CloseButton onClick={handleClose}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </BLightboxGallery.CloseButton>
            {itemTitle && (
              <BLightboxGallery.Title>{itemTitle}</BLightboxGallery.Title>
            )}
          </BLightboxGallery.Header>

          <BLightboxGallery.Body
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <BLightboxGallery.Image
              src={currentPhoto.url}
              alt={itemTitle || ''}
            />

            {showControls && (
              <BLightboxGallery.Controls>
                <BLightboxGallery.ControlButton
                  onClick={handlePrevious}
                  disabled={currentIndex === 0}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </BLightboxGallery.ControlButton>
                <BLightboxGallery.ControlButton
                  onClick={handleNext}
                  disabled={currentIndex === photos.length - 1}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </BLightboxGallery.ControlButton>
              </BLightboxGallery.Controls>
            )}
          </BLightboxGallery.Body>

          <BLightboxGallery.Footer>
            {showGallery && (
              <BLightboxGallery.Gallery size={THUMBNAIL_SIZE} offset={galleryOffset}>
                <BLightboxGallery.GalleryImagesContainer>
                  <BLightboxGallery.GalleryImages>
                    {photos.map((photo, index) => (
                      <BLightboxGallery.GalleryImageContainer
                        key={photo.id || index}
                        selected={currentIndex === index}
                        onClick={() => handleThumbnailClick(index)}
                      >
                        <OptimizedImage
                          src={photo.url}
                          focal={photo.coordinates}
                          alt=""
                          width={`${THUMBNAIL_SIZE}px`}
                          height={`${THUMBNAIL_SIZE}px`}
                        />
                      </BLightboxGallery.GalleryImageContainer>
                    ))}
                  </BLightboxGallery.GalleryImages>
                </BLightboxGallery.GalleryImagesContainer>
              </BLightboxGallery.Gallery>
            )}
          </BLightboxGallery.Footer>
        </BLightboxGallery.Wrapper>
      </BLightboxGallery>
    </StyleSheetManager>
  )

  return createPortal(lightboxContent, document.body)
}

LightboxGallery.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string.isRequired,
    coordinates: PropTypes.object
  })).isRequired,
  selectedIndex: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  itemTitle: PropTypes.string
}

export { LightboxGallery }
